import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { merge, of } from 'rxjs'
import { catchError, concatMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { ToasterService } from '../../shared/toaster/toaster.service'
import * as AuthActions from '../auth/auth.actions'
import { selectAuthUser } from '../auth/auth.selectors'
import { AppAccountsService } from './app-accounts.service'
import * as AppActions from './app.actions'
import { AppsService } from './apps.service'

@Injectable()
export class AppEffects {
  constructor(
    private actions$: Actions,
    private apps: AppsService,
    private accounts: AppAccountsService,
    private store: Store,
    private toaster: ToasterService
  ) {}

  createApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.createApp),
      withLatestFrom(this.store.select(selectAuthUser)),
      concatMap(([action, user]) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
        const { email, ...input } = action.input as any
        return this.apps
          .createApp({
            ...input,
            partnerId: user.partners[0]._id,
            redirectUrls: ['https://docs.tokeet.io'],
            // deletion: { email },
          })
          .pipe(
            map(() => AppActions.createAppSuccess()),
            catchError((error) => of(AppActions.createAppFailure({ error })))
          )
      })
    )
  )

  updateApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.updateApp),
      concatMap((action) =>
        this.apps.updateApp(action.input).pipe(
          map(() => AppActions.updateAppSuccess()),
          catchError((error) => of(AppActions.updateAppFailure({ error })))
        )
      )
    )
  )

  updateAppLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.updateAppLogo),
      concatMap((action) =>
        this.apps.updateApp(action.input).pipe(
          map(() => AppActions.updateAppSuccess()),
          catchError((error) => of(AppActions.updateAppFailure({ error })))
        )
      )
    )
  )

  updateAppWebhookUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.updateAppWebhookUrl),
      concatMap((action) =>
        this.apps.updateApp(action.input).pipe(
          map(() => AppActions.updateAppSuccess()),
          tap(() => this.toaster.success('Webhook URL saved successfully.')),
          catchError((error) => of(AppActions.updateAppFailure({ error })))
        )
      )
    )
  )

  activateApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.activateApp),
      concatMap((action) =>
        this.apps.activateApp({ _id: action.appId }).pipe(
          map(() => AppActions.activateAppSuccess({ appId: action.appId })),
          catchError((error) => of(AppActions.activateAppFailure({ error })))
        )
      )
    )
  )

  deactivateApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.deactivateApp),
      concatMap((action) =>
        this.apps.deactivateApp({ _id: action.appId }).pipe(
          map(() => AppActions.deactivateAppSuccess({ appId: action.appId })),
          catchError((error) => of(AppActions.deactivateAppFailure({ error })))
        )
      )
    )
  )

  disconnectAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.disconnectAccount),
      concatMap((action) =>
        this.accounts.disconnectAccount({ appId: action.appId, account: action.account }).pipe(
          map(() => AppActions.disconnectAccountSuccess({ appId: action.appId, account: action.account })),
          catchError((error) => of(AppActions.disconnectAccountFailure({ error })))
        )
      )
    )
  )

  deleteApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.deleteApp),
      concatMap((action) =>
        this.apps.deleteApp(action.input).pipe(
          map(() => AppActions.deleteAppSuccess()),
          catchError((error) => of(AppActions.deleteAppFailure({ error })))
        )
      )
    )
  )

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      switchMap(() =>
        merge(
          this.apps.listenForAppsChanges().pipe(map((app) => AppActions.receivedApp({ app }))),
          this.apps.listenForAppsDeletion().pipe(map((app) => AppActions.deletedApp({ appId: app._id })))
        )
      )
    )
  )

  createAppFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.createAppFailure),
        tap(() => this.toaster.error('Unable to create application'))
      ),
    { dispatch: false }
  )
  updateAppFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.updateAppFailure),
        tap(() => this.toaster.error('Unable to update application settings'))
      ),
    { dispatch: false }
  )
  activateAppFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.activateAppFailure),
        tap(() => this.toaster.error('Unable to activate application'))
      ),
    { dispatch: false }
  )
  deactivateAppFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.deactivateAppFailure),
        tap(() => this.toaster.error('Unable to deactivate application'))
      ),
    { dispatch: false }
  )
  deleteAppFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.deleteAppFailure),
        tap(() => this.toaster.error('Unable to delete application'))
      ),
    { dispatch: false }
  )
  disconnectAccountFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.disconnectAccountFailure),
        tap(() => this.toaster.error('Unable to disconnect account'))
      ),
    { dispatch: false }
  )
}
